import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Camera, Lock, Sparkles } from 'lucide-react';
import { QuizProfile } from '@/lib/quiz-data';
import { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { PremiumUpsell } from './PremiumUpsell';
import { useAuth } from '@/contexts/AuthContext';

interface QuizResultProps {
  profile: QuizProfile;
}

export const QuizResult = ({ profile }: QuizResultProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Confetti celebration
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  }, []);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Profile Result */}
      <Card className="p-8 bg-gradient-to-br from-primary/10 to-background border-2 border-primary/20">
        <div className="text-center space-y-4">
          <div className="text-6xl mb-4">{profile.icon}</div>
          <h2 className="text-3xl font-bold text-foreground">
            {t('quiz.result.yourProfile')}
          </h2>
          <h3 className="text-2xl font-semibold text-primary">
            {t(profile.name)}
          </h3>
          <p className="text-muted-foreground text-lg">
            {t(profile.description)}
          </p>
        </div>
      </Card>

      {/* Free Tips */}
      <Card className="p-6 border-2 border-border">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg">{t('quiz.result.freeTips')}</h3>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-sm font-medium text-muted-foreground">🎨</span>
              <div>
                <p className="text-sm font-medium">{t('quiz.result.colorsLabel')}</p>
                <p className="text-sm text-muted-foreground">{t(profile.colors)}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <span className="text-sm font-medium text-muted-foreground">✨</span>
              <div>
                <p className="text-sm font-medium">{t('quiz.result.materialsLabel')}</p>
                <p className="text-sm text-muted-foreground">{t(profile.materials)}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Premium Upsell */}
      <PremiumUpsell 
        context="quiz"
        profileName={t(profile.name)}
      />

      {/* CTA Buttons */}
      <div className="space-y-3">
        <Button
          onClick={() => navigate('/camera')}
          size="lg"
          className="w-full h-14 text-lg"
        >
          <Camera className="mr-2 h-5 w-5" />
          {t('quiz.result.evaluateNow')}
        </Button>

        <Button
          onClick={() => navigate('/')}
          variant="outline"
          size="lg"
          className="w-full"
        >
          {t('quiz.result.backHome')}
        </Button>
      </div>

      {/* Social Proof */}
      <div className="text-center text-sm text-muted-foreground">
        <p>{t('quiz.result.socialProof', { count: Math.floor(Math.random() * 500) + 1000 })}</p>
      </div>
    </div>
  );
};
